import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import AuthShell from '../../components/layout/AuthShell';
import Btn from '../../components/ui/Btn';
import { Input, Spinner } from '../../components/ui/primitives';
import { Icon } from '../../components/ui/Icon';
import { ErrorBanner } from './SignInScreen';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useIsMobile } from '../../lib/useBreakpoint';

const WORKSPACE_TYPES = [
  { key: 'client',   label: 'Client onboarding',  desc: 'Collect docs from clients', icon: Icon.users,    industry: 'other' },
  { key: 'vendor',   label: 'Vendor compliance',  desc: 'W-9s, COIs, NDAs',          icon: Icon.shield,   industry: 'other' },
  { key: 'employee', label: 'Employee HR',        desc: 'Hiring paperwork',          icon: Icon.building, industry: 'hr'    },
];

function toSlug(str) {
  return (str || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .substring(0, 40);
}

function passwordStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

export default function RegisterScreen() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { session, loading: authLoading } = useAuth();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Step 1 — account
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');

  // Step 2 — workspace
  const [wsName, setWsName] = useState('');
  const [wsSlug, setWsSlug] = useState('');
  const [wsTypes, setWsTypes] = useState(['client', 'vendor', 'employee']);
  const [createdTeam, setCreatedTeam] = useState(null);

  const slug = wsSlug || toSlug(wsName);
  const pwScore = passwordStrength(pw);

  // If already signed in and they're at step 1, jump them to step 2 (skip account creation)
  useEffect(() => {
    if (!authLoading && session && step === 1) {
      const meta = session.user.user_metadata || {};
      setFirstName(meta.first_name || firstName);
      setLastName(meta.last_name || lastName);
      setEmail(session.user.email || email);
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, authLoading]);

  // ──────────────────────────────────────────────────────────────────────────
  // Step 1 — create account via Supabase
  // ──────────────────────────────────────────────────────────────────────────
  const submitStep1 = async () => {
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your work email.');
      return;
    }
    if (pw.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: pw,
        options: {
          data: { first_name: firstName.trim(), last_name: lastName.trim() },
        },
      });

      if (signUpError) {
        const msg = signUpError.message || '';
        if (/already\s+(been\s+)?registered|already\s+exists/i.test(msg)) {
          throw new Error(
            'An account with this email already exists. ' +
            'If you received an invite, use that link. Otherwise sign in.'
          );
        }
        throw new Error(msg);
      }

      if (!data.session) {
        throw new Error(
          'Account created but no session returned. Please sign in.'
        );
      }

      setStep(2);
    } catch (err) {
      console.error('Sign up failed:', err);
      setError(err.message || 'Something went wrong creating your account.');
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Step 2 — create workspace (team + owner role)
  // ──────────────────────────────────────────────────────────────────────────
  const submitStep2 = async () => {
    setError(null);

    if (!wsName.trim()) {
      setError('Please name your workspace.');
      return;
    }
    if (!slug) {
      setError('Workspace URL must contain at least one letter or number.');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Verify the user can create a team (free=1, pro=3, business=∞)
      const limit = await api('/api/check-team-limit');
      if (!limit.allowed) {
        throw new Error(limit.error || 'You\'ve reached your team limit.');
      }

      // 2. Insert the team. team_roles get auto-created by your DB trigger.
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .insert({ name: wsName.trim(), slug, language_default: 'en' })
        .select()
        .single();

      if (teamError) {
        if ((teamError.message || '').toLowerCase().includes('duplicate')) {
          throw new Error('That workspace name is already taken. Try a different one.');
        }
        throw new Error(teamError.message || 'Failed to create workspace.');
      }

      // 3. Look up the auto-created Owner role for this team
      const { data: roles, error: rolesError } = await supabase
        .from('team_roles')
        .select('id')
        .eq('team_id', team.id)
        .eq('slug', 'owner')
        .limit(1);

      if (rolesError || !roles?.length) {
        throw new Error('Workspace setup failed — owner role not found.');
      }
      const ownerRoleId = roles[0].id;

      // 4. Add the user as a team_member with is_owner = true
      const { error: memberError } = await supabase
        .from('team_members')
        .insert({
          team_id: team.id,
          user_id: session.user.id,
          role_id: ownerRoleId,
          email: session.user.email,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          language: 'en',
          is_owner: true,
        });

      if (memberError) {
        throw new Error(memberError.message || 'Failed to add you to the workspace.');
      }

      setCreatedTeam(team);
      setStep(3);
    } catch (err) {
      console.error('Workspace creation failed:', err);
      setError(err.message || 'Something went wrong creating your workspace.');
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <AuthShell side={{
      topRight: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
          <span style={{ color: 'var(--text-3)' }}>Already have an account?</span>
          <Link to="/sign-in" style={{ textDecoration: 'none' }}>
            <Btn variant="outline" size="sm">Sign in</Btn>
          </Link>
        </div>
      ),
    }}>
      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= step ? 'var(--accent)' : 'var(--border)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--accent-600)',
        letterSpacing: '0.08em', marginBottom: 6,
      }}>
        STEP {step} OF 3
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.025em', marginBottom: 6 }}>
        {step === 1 ? 'Create your account' :
         step === 2 ? 'Name your workspace' :
                      'You\'re all set'}
      </h1>
      <p style={{ fontSize: 13.5, color: 'var(--text-3)', marginBottom: 24 }}>
        {step === 1 ? 'Free forever up to 30 classifications/mo. No credit card.' :
         step === 2 ? 'Invite teammates and clients later from Settings.' :
                      'We\'ve provisioned your workspace. Ready to collect your first doc?'}
      </p>

      {error && <ErrorBanner message={error} style={{ marginBottom: 14 }} />}

      {step === 1 && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 10, marginBottom: 12,
          }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                First name
              </label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                disabled={submitting}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
                Last name
              </label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                disabled={submitting}
              />
            </div>
          </div>

          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            Work email
          </label>
          <Input
            icon={Icon.mail}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            style={{ marginBottom: 12 }}
            disabled={submitting}
          />

          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            Password
          </label>
          <Input
            icon={Icon.lock}
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            style={{ marginBottom: 10 }}
            disabled={submitting}
          />

          {/* Strength meter */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{
                flex: 1, height: 4, borderRadius: 2,
                background: i <= pwScore ? 'var(--accent)' : 'var(--border)',
                transition: 'background 0.2s',
              }} />
            ))}
          </div>

          <Btn
            variant="primary"
            size="lg"
            style={{ width: '100%' }}
            iconRight={submitting ? null : Icon.arrowR}
            onClick={submitStep1}
            disabled={submitting}
          >
            {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Creating account…</> : 'Continue'}
          </Btn>
        </>
      )}

      {step === 2 && (
        <>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            Workspace name
          </label>
          <Input
            icon={Icon.building}
            value={wsName}
            onChange={(e) => { setWsName(e.target.value); setWsSlug(toSlug(e.target.value)); }}
            placeholder="e.g. Northwind Clinics, Acme HR"
            style={{ marginBottom: 12 }}
            disabled={submitting}
          />

          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>
            Workspace URL
          </label>
          <div style={{ display: 'flex', marginBottom: 16 }}>
            <div style={{
              padding: '0 12px', display: 'flex', alignItems: 'center',
              background: 'var(--surface-2)', border: '1px solid var(--border)', borderRight: 'none',
              borderRadius: '10px 0 0 10px', fontSize: 13, color: 'var(--text-3)',
            }}>
              magicintake.com/
            </div>
            <input
              value={slug}
              onChange={(e) => setWsSlug(toSlug(e.target.value))}
              className="mi-focusable mi-input"
              disabled={submitting}
              style={{
                flex: 1, minWidth: 0, height: 40, padding: '0 14px', fontSize: 13.5,
                border: '1px solid var(--border)', borderRadius: '0 10px 10px 0',
                fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)',
              }}
            />
          </div>

          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>
            What do you use MagicIntake for?
          </label>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10 }}>
            Pick one or more — your workspace can handle all of these.
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
            gap: 8, marginBottom: 20,
          }}>
            {WORKSPACE_TYPES.map((t) => {
              const sel = wsTypes.includes(t.key);
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => {
                    if (sel && wsTypes.length === 1) return; // keep at least one
                    setWsTypes(prev => sel ? prev.filter(k => k !== t.key) : [...prev, t.key]);
                  }}
                  disabled={submitting}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 4,
                    padding: '10px 12px', textAlign: 'left',
                    border: `1.5px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                    background: sel ? 'var(--accent-bg)' : 'var(--surface)',
                    borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'border-color 0.12s, background 0.12s',
                    position: 'relative',
                  }}
                >
                  {sel && (
                    <div style={{
                      position: 'absolute', top: 8, right: 8,
                      width: 16, height: 16, borderRadius: '50%',
                      background: 'var(--accent)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon.check size={10} stroke="#fff" strokeWidth={3} />
                    </div>
                  )}
                  <t.icon size={15} stroke={sel ? 'var(--accent-600)' : 'var(--text-3)'} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{t.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{t.desc}</div>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="outline" size="lg" style={{ flex: 1 }} onClick={() => setStep(1)} disabled={submitting}>
              Back
            </Btn>
            <Btn
              variant="primary"
              size="lg"
              style={{ flex: 2 }}
              iconRight={submitting ? null : Icon.arrowR}
              onClick={submitStep2}
              disabled={submitting}
            >
              {submitting ? <><Spinner size={14} color="rgba(255,255,255,0.6)" /> Creating workspace…</> : 'Create workspace'}
            </Btn>
          </div>
        </>
      )}

      {step === 3 && createdTeam && (
        <>
          <div style={{
            width: 64, height: 64, borderRadius: 20,
            background: 'var(--accent-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 20, boxShadow: '0 0 0 8px rgba(61,199,91,0.1)',
          }}>
            <Icon.check size={32} stroke="var(--accent-600)" strokeWidth={2.5} />
          </div>

          <div style={{
            padding: 16, background: 'var(--surface-2)',
            border: '1px solid var(--border)', borderRadius: 12, marginBottom: 20,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: 4 }}>
              YOUR WORKSPACE
            </div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{createdTeam.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              magicintake.com/{createdTeam.slug}
            </div>
          </div>

          <Btn
            variant="primary"
            size="lg"
            style={{ width: '100%', marginBottom: 10 }}
            iconRight={Icon.arrowR}
            onClick={() => navigate('/hub')}
          >
            Enter workspace
          </Btn>
          <Btn
            variant="ghost"
            size="md"
            style={{ width: '100%' }}
            onClick={() => alert('Tour coming soon!')}
          >
            Take the 2-min tour
          </Btn>
        </>
      )}
    </AuthShell>
  );
}
